import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { UPDATE_UNIT } from '../../../Reducers/Actions';
import { Box } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../theme';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import FormInput from '../../../SystemDesign/FormInput';
import { useUpdateUnitMutation } from '../../../services/SkillsManagement';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import EditIcon from '@mui/icons-material/Edit';
import ColoredSelector from '../../../SystemDesign/ColoredSelector';

const UpdateUnit = ({ open, onClose, unit, disciplines = [], onSubmit }) => {
  const dispatch = useDispatch();
  
  // Access the SnackBar context
  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // State to track form validity and dirty state
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  
  // Create a ref to access Formik methods from outside the Formik component
  const formRef = useRef(null);
  
  // Form state
  const [formError, setFormError] = useState('');
  const [selectedDisciplineId, setSelectedDisciplineId] = useState(unit?.discipline_id || '');
  
  // RTK Query mutation hook
  const [updateUnitMutation, { isLoading }] = useUpdateUnitMutation();
  
  // Prepare initial form values
  const getInitialValues = () => ({
    code: unit?.code || unit?.unit_code || '',
    title: unit?.title || unit?.specific_unit || '',
    discipline_id: unit?.discipline_id || ''
  });
  
  // Set form values state
  const [formValues, setFormValues] = useState(getInitialValues());
  
  // Update form values when unit changes
  useEffect(() => {
    if (unit) {
      setFormValues(getInitialValues());
      setSelectedDisciplineId(unit.discipline_id || '');
    }
  }, [unit]);
  
  // Validation schema
  const validationSchema = Yup.object({
    code: Yup.string().required('Unit code is required'),
    title: Yup.string().required('Unit title is required'),
    discipline_id: Yup.mixed().required('Departement is required')
  });
  
  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    if (!unit) return;
    
    try {
      setFormError('');
      
      // Ensure discipline_id is properly formatted (number) for the backend API
      const discipline_id = values.discipline_id ? parseInt(values.discipline_id, 10) : 0;
      
      // Call the API to update the unit
      const result = await updateUnitMutation({
        unitId: unit.unit_id,
        body: {
          unit_code: values.code.trim(),
          specific_unit: values.title.trim(),
          discipline_id: discipline_id
        }
      }).unwrap();
      
      // Prepare the unit for Redux store with consistent field names
      const updatedUnitForStore = {
        ...result,
        unit_id: unit.unit_id, // Ensure ID is included
        code: values.code.trim(), // Use consistent field names for code
        title: values.title.trim(), // Use consistent field names for title
        discipline_id: discipline_id
      };
      
      // Update Redux store
      dispatch({ type: UPDATE_UNIT, payload: updatedUnitForStore });
      
      // Show success message
      setInfoAlert({
        severity: 'success',
        message: `Unit "${values.code.trim()} - ${values.title.trim()}" successfully updated`
      });
      setOpenSnackBar(true);
      
      // If the onSubmit prop exists, call it with the updated unit
      if (onSubmit) {
        onSubmit(updatedUnitForStore);
      }
      
      // Close dialog
      onClose();
    } catch (error) {
      console.error('Failed to update unit:', error);
      const errorMessage = error?.data?.message || 'Failed to update unit';
      
      // Set form error
      setFormError(errorMessage);
      
      // Also show in snackbar for better visibility
      setInfoAlert({
        severity: 'error',
        message: errorMessage
      });
      setOpenSnackBar(true);
      
      setSubmitting(false);
    }
  };

  // Create dialog content for MuiDialog
  const dialogContent = () => (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
      validateOnMount={true}
      enableReinitialize={true}
    >
      {(formikProps) => {
        // Update parent about form state changes without using hooks
        const { isValid, dirty, setFieldValue } = formikProps;
        if (formRef.current && (formRef.current.isValid !== isValid || formRef.current.dirty !== dirty)) {
          setTimeout(() => {
            setIsFormValid(isValid);
            setIsFormDirty(dirty);
          }, 0);
        }
        
        // Handle discipline selection
        const handleDisciplineChange = (disciplineId) => {
          setSelectedDisciplineId(disciplineId);
          setFieldValue('discipline_id', disciplineId);
        };
        
        return (
          <Form>
            <Box sx={{ mb: 2 }}>
              <FormInput
                name="code"
                label="Unit Code"
                placeholder="Enter unit code"
                required
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <FormInput
                name="title"
                label="Unit Title"
                placeholder="Enter unit title"
                required
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <ColoredSelector
                options={disciplines}
                value={formikProps.values.discipline_id}
                onChange={handleDisciplineChange}
                placeholder="Select a departement"
                label="Departement"
                colorKey="color"
                valueKey="discipline_id"
                labelKey="name"
                searchable={true}
                required={true}
                error={formikProps.touched.discipline_id && Boolean(formikProps.errors.discipline_id)}
                helperText={formikProps.touched.discipline_id && formikProps.errors.discipline_id}
              />
            </Box>
            
            {formError && (
              <Box sx={{ color: 'error.main', mb: 2 }}>
                {formError}
              </Box>
            )}
          </Form>
        );
      }}
    </Formik>
  );
  
  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      icon={<EditIcon />}
      title="Edit Unit"
      subtitle="Update the unit details"
      content={dialogContent()}
      maxWidth="xs"
      existAction={true}
      actionTitle="Update"
      actionType="submit"
      actionFunction={() => {
        if (formRef.current) {
          formRef.current.handleSubmit();
        }
      }}
      isLoading={isLoading}
      disableAction={!isFormValid || !isFormDirty}
    />
  );
};

export default UpdateUnit;